import { db, type LocalShift, type LocalCashMovement, type LocalAuditEntry } from "./db";
import {
  commitAtomically,
  getOpenShift,
  getSalesForShift,
  getCashMovements,
  getAuditEntriesForShift,
  getVoidsForShift,
  getRefundsForShift,
} from "./repositories";
import { uuidv7 } from "../../platform/id";
import { money, type Money } from "../../domain/money/money";
import { businessDate } from "../../domain/time";
import {
  closeShift as computeClose,
  varianceRequiresAuthorization,
} from "../../domain/shift/expected-cash";
import { computeReading, type ReadingSource, type Reading } from "../../domain/shift/reading";
import { countCups, type CupCount } from "../../domain/shift/cups";
import { coreEvents } from "../../domain/events/bus";
import { isCashTender } from "../../domain/journal/finalize";

/**
 * Shift lifecycle on the terminal.
 *
 * Every one of these writes goes through `commitAtomically`, so a shift open or
 * close is as durable and as offline-capable as a sale (Principle I).
 */

export class ShiftLifecycleError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ShiftLifecycleError";
  }
}

export interface OpenShiftInput {
  terminalId: string;
  staffId: string;
  openingFloat: Money;
  timezone: string;
}

export async function openShift(input: OpenShiftInput): Promise<LocalShift> {
  const existing = await getOpenShift(input.terminalId);
  if (existing) {
    throw new ShiftLifecycleError("A shift is already open on this terminal");
  }

  const now = new Date();
  const shift: LocalShift = {
    id: uuidv7(),
    terminalId: input.terminalId,
    openedBy: input.staffId,
    openedAt: now.toISOString(),
    openingFloat: input.openingFloat,
    closedBy: null,
    closedAt: null,
    countedCash: null,
    expectedCash: null,
    variance: null,
    varianceReason: null,
    varianceAuthorizedBy: null,
    status: "open",
    businessDate: businessDate(now, input.timezone),
  };

  await commitAtomically({
    shift,
    envelope: {
      opId: uuidv7(),
      type: "shift.open",
      payload: { shiftId: shift.id, shift },
      createdAt: shift.openedAt,
      terminalId: input.terminalId,
      attempts: 0,
      state: "pending",
      lastError: null,
      nextAttemptAt: null,
    },
  });

  await coreEvents.emit("shift.opened", {
    opId: shift.id,
    shiftId: shift.id,
    terminalId: shift.terminalId,
    staffId: shift.openedBy,
    createdAt: shift.openedAt,
  });

  return shift;
}

/** Gather everything a reading needs from the local store. */
export async function buildReadingSource(shiftId: string): Promise<ReadingSource> {
  const shift = await db.shifts.get(shiftId);
  if (!shift) throw new ShiftLifecycleError(`Unknown shift ${shiftId}`);

  const sales = await getSalesForShift(shiftId);
  const movements = await getCashMovements(shiftId);
  const audits = await getAuditEntriesForShift(shiftId);
  const voids = await getVoidsForShift(shiftId);
  const refunds = await getRefundsForShift(shiftId);

  const documentNumbers = new Map(sales.map((sale) => [sale.id, sale.documentNumber]));
  const saleTotals = new Map(sales.map((sale) => [sale.id, sale.total]));

  const salesWithTenders = await Promise.all(
    sales.map(async (sale) => {
      const tenders = await db.tenders.where("saleId").equals(sale.id).toArray();
      return {
        documentNumber: sale.documentNumber,
        total: sale.total,
        subtotal: sale.subtotal,
        taxAmount: sale.taxAmount,
        discountTotal: sale.discountTotal,
        statutoryDiscountTotal: sale.statutoryDiscountTotal,
        status: sale.status,
        tenders: tenders.map((tender) => ({
          type: tender.type,
          amount: tender.amount,
          changeGiven: tender.changeGiven,
        })),
      };
    }),
  );

  return {
    shiftId,
    terminalId: shift.terminalId,
    businessDate: shift.businessDate,
    openingFloat: shift.openingFloat,
    sales: salesWithTenders,
    // Derived from the correction records themselves, never from a status
    // written back onto the sale — the sale row is append-only (FR-021).
    voids: voids.map((record) => ({
      saleId: record.saleId,
      documentNumber: documentNumbers.get(record.saleId) ?? "",
      amount: saleTotals.get(record.saleId) ?? money(0),
    })),
    refunds: refunds.map((record) => ({
      saleId: record.saleId,
      amount: record.totalAmount,
      isCash: record.isCash,
    })),
    cashMovements: movements.map((movement) => ({
      direction: movement.direction,
      amount: movement.amount,
    })),
    // Core cash-control counts. Present whether or not any peripheral module
    // exists (FR-041b) — the drawer module adds hardware detail, not this.
    noSaleCount: audits.filter((entry) => entry.action === "drawer.no_sale").length,
    failedDrawerReleaseCount: 0,
  };
}

export async function readXReading(shiftId: string): Promise<Reading> {
  return computeReading(await buildReadingSource(shiftId), "x");
}

/** Cups sold so far this shift, by size. Live, like an X reading. */
export async function readCupsSold(shiftId: string): Promise<{ sizes: CupCount[]; total: number }> {
  const sales = await getSalesForShift(shiftId);
  const voids = await getVoidsForShift(shiftId);
  const lines = await db.saleLines
    .where("saleId")
    .anyOf(sales.map((sale) => sale.id))
    .toArray();
  return countCups(lines, new Set(voids.map((record) => record.saleId)));
}

export interface CloseShiftInput {
  shiftId: string;
  staffId: string;
  countedCash: Money;
  varianceThreshold: Money;
  varianceReason?: string | null;
  varianceAuthorizedBy?: string | null;
}

export async function closeShiftLocally(input: CloseShiftInput): Promise<{
  shift: LocalShift;
  reading: Reading;
}> {
  const shift = await db.shifts.get(input.shiftId);
  if (!shift) throw new ShiftLifecycleError(`Unknown shift ${input.shiftId}`);
  if (shift.status === "closed") {
    throw new ShiftLifecycleError("This shift is already closed");
  }

  const source = await buildReadingSource(input.shiftId);
  const reading = computeReading(source, "z");

  const cashSales = Object.entries(reading.tenderBreakdown)
    .filter(([type]) => isCashTender(type))
    .reduce((total, [, amount]) => total + amount, 0);

  const result = computeClose(
    {
      openingFloat: shift.openingFloat,
      cashSales: money(cashSales),
      // Cash handed back reduces what should be in the drawer. Leaving this at
      // zero made every close after a cash refund look over by that amount.
      cashRefunds: money(
        source.refunds.filter((r) => r.isCash).reduce((total, r) => total + r.amount, 0),
      ),
      payIns: money(
        source.cashMovements.filter((m) => m.direction === "in").reduce((t, m) => t + m.amount, 0),
      ),
      payOuts: money(
        source.cashMovements.filter((m) => m.direction === "out").reduce((t, m) => t + m.amount, 0),
      ),
    },
    input.countedCash,
  );

  if (
    varianceRequiresAuthorization(result.variance, input.varianceThreshold) &&
    !input.varianceAuthorizedBy
  ) {
    throw new ShiftLifecycleError(
      "This variance exceeds the threshold and needs a manager's authorization and a reason",
    );
  }

  const now = new Date().toISOString();
  const closed: LocalShift = {
    ...shift,
    closedBy: input.staffId,
    closedAt: now,
    countedCash: result.counted,
    expectedCash: result.expected,
    variance: result.variance,
    varianceReason: input.varianceReason ?? null,
    varianceAuthorizedBy: input.varianceAuthorizedBy ?? null,
    status: "closed",
  };

  await commitAtomically({
    shift: closed,
    envelope: {
      opId: uuidv7(),
      type: "shift.close",
      payload: { shiftId: closed.id, shift: closed, reading },
      createdAt: now,
      terminalId: closed.terminalId,
      attempts: 0,
      state: "pending",
      lastError: null,
      nextAttemptAt: null,
    },
  });

  await coreEvents.emit("shift.closed", {
    opId: closed.id,
    shiftId: closed.id,
    terminalId: closed.terminalId,
    staffId: input.staffId,
    createdAt: now,
  });

  return { shift: closed, reading };
}

// ---------------------------------------------------------------------------
// Cash movements and the no-sale drawer open
// ---------------------------------------------------------------------------

export async function recordCashMovement(input: {
  shiftId: string;
  terminalId: string;
  direction: "in" | "out";
  amount: Money;
  reason: string;
  staffId: string;
}): Promise<LocalCashMovement> {
  if (input.amount <= 0) {
    throw new ShiftLifecycleError("A cash movement must be greater than zero");
  }

  const movement: LocalCashMovement = {
    id: uuidv7(),
    shiftId: input.shiftId,
    direction: input.direction,
    amount: input.amount,
    reason: input.reason,
    staffId: input.staffId,
    createdAt: new Date().toISOString(),
  };

  await commitAtomically({
    cashMovement: movement,
    envelope: {
      opId: uuidv7(),
      type: "cash.movement",
      payload: movement,
      createdAt: movement.createdAt,
      terminalId: input.terminalId,
      attempts: 0,
      state: "pending",
      lastError: null,
      nextAttemptAt: null,
    },
  });

  return movement;
}

/**
 * A no-sale drawer open — THE CORE CASH-CONTROL RECORD.
 *
 * This records that a person was AUTHORIZED to reach the cash, and why. It is
 * written and counted whether or not any peripheral module is enabled and
 * whether or not any hardware exists (FR-041b). The peripherals module
 * subscribes to the event below in order to attempt a physical release, and
 * records only what the hardware did — it never duplicates the reason or the
 * authoriser.
 *
 * Deliberately an `audit_entry`, never a `cash_movement`: no money moves, and
 * giving it an amount would corrupt expected-cash arithmetic.
 */
export async function recordNoSale(input: {
  shiftId: string;
  terminalId: string;
  staffId: string;
  authorizedBy: string | null;
  reason: string;
}): Promise<LocalAuditEntry> {
  if (!input.reason.trim()) {
    throw new ShiftLifecycleError("A no-sale drawer open requires a reason");
  }

  const entry: LocalAuditEntry = {
    id: uuidv7(),
    entityType: "shift",
    entityId: input.shiftId,
    action: "drawer.no_sale",
    shiftId: input.shiftId,
    staffId: input.staffId,
    authorizedBy: input.authorizedBy,
    reason: input.reason,
    detail: null,
    createdAt: new Date().toISOString(),
  };

  const opId = uuidv7();

  await commitAtomically({
    auditEntry: entry,
    envelope: {
      opId,
      type: "drawer.no_sale",
      payload: entry,
      createdAt: entry.createdAt,
      terminalId: input.terminalId,
      attempts: 0,
      state: "pending",
      lastError: null,
      nextAttemptAt: null,
    },
  });

  // The module, if enabled, opens the drawer in response to this. If no module
  // is enabled the record still exists and is still counted.
  await coreEvents.emit("drawer.noSaleAuthorized", {
    opId,
    auditEntryId: entry.id,
    shiftId: input.shiftId,
    terminalId: input.terminalId,
    staffId: input.staffId,
    authorizedBy: input.authorizedBy,
    reason: input.reason,
    createdAt: entry.createdAt,
  });

  return entry;
}
